import {
  Collapse,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import clientApi from '~/clientApi';
import { CreatorAgencyContentSelected } from '~/clientApi/creator.model';
import {
  ContentSearchFilter,
  ContentSearchTrackIdComponentType,
} from '~/components/ContentSearchFilter';
import { ContentSearchPicker } from '~/components/ContentSearchPicker';
import { ContentSearchPickerSelected } from '~/components/ContentSearchPickerSelected';
import Pagination from '~/components/Pagination/Pagination';
import { getMainPlatformData } from '~/core/utils';
import {
  CONTENT_SEARCH_PARAMS,
  CONTENT_SEARCH_ROSTER,
  useContentSearch,
} from '~/hooks/useContentSearch';
import { ISearchContentResult } from '~/searchApi';
import { ICreator } from '~/types';

import {
  CONTENT_SEACH_PAGE_SIZE,
  CONTENT_SEARCH_LIMIT,
} from './ContentSearchPickerModal.constants';
import {
  ContentSearchPickerModalHeader,
  ContentSearchPickerModalLimit,
} from './components';

export interface IContentSearchPickerModal {
  creator: ICreator;
  isOpen: boolean;
  onClose: () => void;
  onError?: () => void;
  onSave?: (selected: CreatorAgencyContentSelected[]) => Promise<any>;
  onSuccess?: (creator: ICreator) => void;
  trackIds?: ContentSearchTrackIdComponentType;
}

export const ContentSearchPickerModal: React.FC<IContentSearchPickerModal> = ({
  creator,
  isOpen,
  onClose,
  onError,
  onSave,
  onSuccess,
  trackIds,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedContent, setSelectedContent] = useState<
    ISearchContentResult[]
  >(
    creator.highlightedContent?.length
      ? creator.highlightedContent ?? []
      : creator.contentAgencySelected ?? [],
  );
  const { name = 'Creator' } = getMainPlatformData(creator);

  const isDirty = !isEqual(
    creator.highlightedContent
      ? creator.highlightedContent
      : creator.contentAgencySelected,
    selectedContent,
  );
  const isLimitReached = selectedContent.length === CONTENT_SEARCH_LIMIT;
  const modalElementRef = useRef<HTMLDivElement>(null);
  const selectedContentIds = selectedContent.map(({ contentId }) => contentId);

  const { contents, isFetching, query, setQuery, totalItems } =
    useContentSearch({
      defaultQuery: {
        [CONTENT_SEARCH_PARAMS.creator]: creator?.id,
        [CONTENT_SEARCH_PARAMS.roster]: CONTENT_SEARCH_ROSTER.agency,
        [CONTENT_SEARCH_PARAMS.page_size]: CONTENT_SEACH_PAGE_SIZE.toString(),
      },
      isEnabled: Boolean(creator),
      ignoreRosterParam: true,
    });

  const totalPages: number = Math.floor(
    (totalItems ?? 0) / CONTENT_SEACH_PAGE_SIZE,
  );

  const handleClick = (content: ISearchContentResult) => {
    const isSelected = selectedContentIds.includes(content.contentId);
    const updatedSelectedContent = isSelected
      ? [...selectedContent].filter(
          ({ contentId }) => contentId !== content.contentId,
        )
      : [...selectedContent, content];
    setSelectedContent(updatedSelectedContent);
  };

  const selected: CreatorAgencyContentSelected[] = useMemo(
    () =>
      selectedContent.map(({ contentId, ...content }) => ({
        ...content,
        id: contentId,
      })),
    [selectedContent],
  );

  const handleSave = useCallback(async () => {
    const handleUpdate = (): Promise<ICreator> => {
      if (onSave) {
        return onSave(selected);
      }

      return clientApi.creators.update({
        id: creator?.id,
        contentAgencySelected: selected,
      });
    };

    try {
      setIsSaving(true);
      const updatedCreator = await handleUpdate();
      setIsSaving(false);
      return onSuccess && onSuccess(updatedCreator);
    } catch (error) {
      return onError && onError();
    }
  }, [creator?.id, onError, onSave, onSuccess, selected]);

  const handlePagination = (page: number) => {
    setQuery({
      ...query,
      [CONTENT_SEARCH_PARAMS.page]: page.toString(),
    });

    /**
     * There seems to be no way to get the container, adding
     * the ref is a accepted prop, but it doesnt work, first
     * element to work was the body and have to move up to get
     * to the element I want to scroll to top.
     */
    if (modalElementRef.current) {
      modalElementRef.current.parentElement?.parentElement?.scrollTo(0, 0);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxWidth="90vw">
        <ModalBody paddingX={6} paddingY={1} ref={modalElementRef}>
          <ContentSearchPickerModalHeader
            isDisabled={!isDirty}
            isLoading={isSaving}
            name={name}
            onCancel={onClose}
            onSave={handleSave}
          />
          <ContentSearchPickerSelected
            onClick={handleClick}
            selectedContent={selectedContent}
          />
          <Collapse in={isLimitReached}>
            <ContentSearchPickerModalLimit />
          </Collapse>

          <ContentSearchFilter
            filters={query}
            onChange={setQuery}
            opacity={isLimitReached ? 0.2 : undefined}
            pointerEvents={isLimitReached ? 'none' : undefined}
            trackIds={trackIds}
            order={{
              [CONTENT_SEARCH_PARAMS.ads]: 0,
              [CONTENT_SEARCH_PARAMS.query]: 1,
              [CONTENT_SEARCH_PARAMS.sortBy]: 2,
              [CONTENT_SEARCH_PARAMS.platform]: 3,
            }}
            enabled={{
              [CONTENT_SEARCH_PARAMS.ads]: true,
            }}
          />

          <ContentSearchPicker
            contents={contents}
            isLoading={isFetching}
            limit={CONTENT_SEARCH_LIMIT}
            marginBottom={6}
            onClick={handleClick}
            opacity={isLimitReached ? 0.2 : undefined}
            pointerEvents={isLimitReached ? 'none' : undefined}
            selectedContent={selectedContent}
          />

          {!isFetching && totalPages > 1 && (
            <Pagination
              count={totalPages}
              marginBottom={4}
              onChange={handlePagination}
              opacity={isLimitReached ? 0.2 : undefined}
              page={parseFloat(query.page ?? '1')}
              pointerEvents={isLimitReached ? 'none' : undefined}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
